/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {MutableRefObject, useCallback} from 'react';
import isNil from 'lodash.isnil';
import sortby from 'lodash.sortby';
import uniqBy from 'lodash.uniqby';
import moment from 'moment';
import {normalizeAssetStatus} from '@ion/core/src/services/ArcGISUtils';
import {resolveHistoryEvents} from '@ion/core/src/services/ConnectUtils';
import Device from '@ion/core/src/services/Device';
import {
  BaseAsset,
  CVSAssetType,
  FindNearbyType,
  GenericObject,
  HVSRecord, SCDeviceType
} from '@ion/core/src/types/entities';
import {convertOdometer} from '@ion/core/src/units/helpers';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {toStringMapper} from '@ion/core/src/utils/StringUtil';
import {HISTORY_MIN_DAYS} from '@ion/dls/src/maps/components/timeMode/current/constants';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {
  BoundaryMode,
  TimeModes,
  TrackingFilters,
  TrackingMode
} from '@ion/dls/src/types/types';
import {TrackingContainerHeightOffset} from '../../types/pages';

export const CATEGORY_DEFINITION = 'assetCategory <> \'\' AND assetCategory IS NOT NULL';
export const ROUTE_DEFINITION    = 'routeName <> \'\' AND routeName IS NOT NULL';

export interface TrackingStyleOptions {
  isPhone: boolean;
  showFilterContainer?: boolean;
}

export const ASSET_MAPPING = {
  id            : 'assetId',
  assetId       : 'assetId',
  title         : 'assetName',
  assetType     : 'assetType',
  assetCategory : 'assetCategory',
  routeId       : 'routeId',
  routeName     : 'routeName'
};

export const CATEGORY_MAPPING = {
  id            : 'assetCategory',
  assetCategory : 'assetCategory',
  title         : 'assetCategory'
};

export const ROUTE_MAPPING = {
  id      : 'routeId',
  routeId : 'routeId',
  title   : 'routeName'
};

export const getTrackingStyle = (
  isFilterSelected: boolean,
  isDesktopTopSheetOpen: boolean,
  {
    isPhone,
    showFilterContainer = true
  }: TrackingStyleOptions
) => {
  if (!showFilterContainer) {
    return {
      height     : '100%',
      transition : 'height 400ms cubic-bezier(0, 1, 0, 1)',
      top        : 0
    };
  }
  const adjustMapHeight = isFilterSelected
    ? isDesktopTopSheetOpen ? TrackingContainerHeightOffset.FILTER_SELECTED_BAR_OPEN
      : TrackingContainerHeightOffset.FILTER_SELECTED_BAR_COLLAPSED
    : isDesktopTopSheetOpen ? TrackingContainerHeightOffset.NO_FILTER_BAR_OPEN
      : TrackingContainerHeightOffset.NO_FILTER_BAR_COLLAPSED;
  const offsetHeight = isPhone ? TrackingContainerHeightOffset.MOBILE_DEFAULT : adjustMapHeight;
  return {
    height     : `calc(100vh - ${offsetHeight}px)`,
    transition : 'height 400ms cubic-bezier(0, 1, 0, 1)',
    top        : isDesktopTopSheetOpen ? (isFilterSelected ? '0px': 0) : 0
  };
};

export const getValuebyField = (
  data: GenericObject[],
  field: string,
  formatter?: Function) => data?.map((item) => formatter?.(item?.[field]) ?? item?.[field]);
const validateOperator = (definition: string, operator: string = ' AND ') => definition?.length ? operator : definition;

/**
 * Builds the clause the Fleet layer leaves a running timeline's assets out
 * under - the Live History layer draws those assets instead, so the two are
 * never drawn over one another.
 *
 * ```typescript
 *
 *   import {toLiveHistoryExclusion} from '@/routes/tracking2/helpers';
 *   ...
 *
 *   const exclusion = toLiveHistoryExclusion([9, 11]);
 *   // 'assetId NOT IN (9,11)' ...
 *
 * ```
 *
 * @param   assetIds The ids of the assets the running timeline draws.
 * @returns The where clause that leaves those assets out.
 */
export const toLiveHistoryExclusion = (assetIds: Array<string | number>): string => (
  `assetId NOT IN (${assetIds})`
);

type AssetDefinitionProps = {
  groups?: GenericObject[],
  assets?: GenericObject[],
  devices?: GenericObject[],
  geofences?: GenericObject[],
  routes?: GenericObject[],
  assetTypes?: string[],
  assetCategories?: string[],
  assetStatus?: string[],
  filteredIds?: number[],
  gridFilterApplied?: boolean
};

/**
 * Defines function will format as per SQL rule
 */
export const formatter = (value: string) => value.replace(/'/g, '\'\'');

/**
 * Defines normalizeGeoFence which is used to normalize geofences with names
 */
const normalizeGeoFence = (list: string[]) => {
  list = list.map(item => item.replace(item, `LOWER(location) LIKE '%${item?.toLowerCase()}%'`));
  return `(${list.join(' OR ')})`;
};

/**
 * @getDefinitionExpression
 * 
 * Defines a utility method create definition expression based on given parameters
 * 
 * import {getDefinitionExpression} from '@/routes/tracking2/helpers';
 * 
 * const definition = getDefinitionExpression(params);
 */
export const getDefinitionExpression = ({
  groups,
  assets,
  devices,
  routes,
  geofences,
  assetTypes,
  assetCategories,
  assetStatus,
  filteredIds,
  gridFilterApplied = false
}: AssetDefinitionProps) => {
  if (gridFilterApplied) {
    return `assetId IN (${filteredIds})`;
  }
  let definition = '';
  if (groups?.length) {
    definition += `accountId IN (${getValuebyField(groups, 'id')})`;
  }
  if (assets?.length) {
    const hasOtherFilters = !!definition?.length;
    const ids = getValuebyField(assets, 'id');
    definition += validateOperator(definition, ' OR ');
    definition += `assetId IN (${ids})`;
    definition = hasOtherFilters ? `(${definition})` : definition;
  }
  if (devices?.length) {
    const hasOtherFilters = !!definition?.length;
    const ids = getValuebyField(devices, 'id');
    definition += validateOperator(definition, ' OR ');
    definition += `deviceId IN (${ids})`;
    definition = hasOtherFilters ? `(${definition})` : definition;
  }
  if (routes?.length) {
    definition += validateOperator(definition);
    definition += `routeId IN (${getValuebyField(routes, 'id')})`;
  }
  if (geofences?.length) {
    definition += validateOperator(definition);
    const values = getValuebyField(geofences, 'name', formatter);
    definition += normalizeGeoFence(values);
  }
  if (assetTypes?.length) {
    definition += validateOperator(definition);
    definition += `LOWER(assetType) IN (${toStringMapper(assetTypes, formatter, true)})`;
  }
  if (assetCategories?.length) {
    definition += validateOperator(definition);
    definition += `LOWER(assetCategory) IN (${toStringMapper(assetCategories, formatter, true)})`;
  }
  if (assetStatus?.length) {
    definition += validateOperator(definition);
    definition += `(${normalizeAssetStatus(assetStatus)})`;
  }
  return definition?.length ? definition : '1 = 1';
};

/**
 * Builds the where clause the Fleet layer shows its assets under, from the
 * tracking filters as applied - the Groups / Assets tree and every other
 * category - or from the grid's own filter once that is applied in their
 * place. It is the one reading of "what is on the map" the Fleet layer draws
 * with and a timeline reports over, so the two can never disagree.
 *
 * ```typescript
 *
 *   import {getFleetDefinitionExpression} from '@/routes/tracking2/helpers';
 *   ...
 *
 *   const where = getFleetDefinitionExpression(filters, filteredIds, filterApplied);
 *
 * ```
 *
 * @param   filters           The tracking filters as applied.
 * @param   filteredIds       The asset ids the grid's own filter leaves.
 * @param   gridFilterApplied Whether the grid's own filter is applied.
 * @returns The where clause, `'1 = 1'` where nothing is filtered.
 */
export const getFleetDefinitionExpression = (
  filters: TrackingFilters,
  filteredIds?: Array<string | number>,
  gridFilterApplied = false
): string => (
  getDefinitionExpression({
    groups          : filters.groups.selected,
    assets          : filters.assets.selected,
    devices         : filters.devices.selected,
    routes          : filters.routes.selected,
    geofences       : filters.geoFences.selected,
    assetTypes      : filters.assetTypes.selected,
    assetCategories : filters.assetCategories.selected,
    assetStatus     : filters.assetStatus.selected,
    gridFilterApplied,
    filteredIds     : [...(filteredIds || [])].sort()
  } as any)
);

/**
 * @getSelectedAsset
 * 
 * Defines a utility to return selected Asset (Asset or Device)
 * 
 * import {getSelectedAsset} from '@/routes/tracking2/helpers';
 * 
 * const selectedAsset = getSelectedAsset(selected);
 */
export const getSelectedAsset = (selected: CVSAssetType | SCDeviceType) => {
  const {isScDevice} = selected || {};
  return isScDevice && isTrue(isScDevice)
    ? Device.getDeviceFromLayer(selected)
    : Device.getAssetsFromCollection([selected]);
};

/**
 * @getSaveMapViewExtent
 * 
 * Defines a utility to return the extent from the MapView saved as default
 * 
 * import {getSaveMapViewExtent} from '@/routes/tracking2/helpers';
 * 
 * const extent = getSaveMapViewExtent(savedMapView);
 */
export const getSaveMapViewExtent = (savedMapView: GenericObject) => {
  const {extent} = savedMapView || {};
  return extent ? [{extent: extent}] : null;
};

/**
 * @getFindNearbyAsset
 * 
 * Defines a utility to return an object based on Find Nearby format
 * 
 * import {getFindNearbyAsset} from '@/routes/tracking2/helpers';
 * 
 * const fnAsset = getFindNearbyAsset(selectedAsset);
 */
export const getFindNearbyAsset = (selectedAsset: BaseAsset) => {
  if (selectedAsset) {
    const {assetId, assetName, latitude, longitude} = selectedAsset;
    const asset = {
      type     : FindNearbyType.asset,
      id       : assetId,
      name     : assetName,
      location : {
        latitude,
        longitude
      }
    };
    return asset;
  }
  return null;
};

/**
 * @getFindNearbyGeofence
 * 
 * Defines a utility to return an object based on Find Nearby format
 * 
 * import {getFindNearbyGeofence} from '@/routes/tracking2/helpers';
 * 
 * const fnGeofence = getFindNearbyGeofence(geofence);
 */
export const getFindNearbyGeofence = (geofence: __esri.Graphic) => {
  const {geometry, attributes} = geofence || {};
  if (geometry && attributes) {
    const {type, centroid} = geometry as any;
    if (type === 'polygon') {
      const {landmarkName} = attributes;
      const geofence = {
        type     : FindNearbyType.geofence,
        name     : landmarkName,
        location : {
          latitude  : centroid.latitude,
          longitude : centroid.longitude
        }
      };
      return geofence;
    }
  }
  return null;
};

export const getFindNearbyLocation = location => {
  if (location) {
    const {label, latitude, longitude} = location;
    const place = {
      type     : FindNearbyType.location,
      name     : label,
      location : {
        latitude,
        longitude
      }
    };
    return place;
  }
  return null;
};

/**
 * Extracts the last page of asset data from a paginated response object.
 * - If the `pages` array exists and has elements, returns the last page.
 * - Otherwise, returns an empty object.
 *
 * @param data - The paginated data object potentially containing a `pages` array.
 * @returns The last page from the `pages` array, or an empty object if unavailable.
 */
export const getLastAssetPage = data => {
  const {pages} = data || {};
  if (Array.isArray(pages) && pages?.length > 0) {
    return pages[pages.length - 1];
  }
  return {};
};

/**
 * Splits a page of asset history into one `assetsRoutesHistory` entry per
 * asset, keyed `id-<assetId>`.
 * - Pages merged on a first load or a refresh carry several assets at once;
 *   each asset still gets its own entry, so a later page for any one of them
 *   replaces its records rather than drawing them a second time.
 * - Every entry carries the page's routes whole - route keys identify their
 *   own request, so entries sharing them collapse back into one per route.
 * - A record with no asset of its own is filed under the page's first asset.
 *
 * ```typescript
 * import {groupAssetsRoutesHistoryByAsset} from '@/routes/tracking2/helpers';
 *
 * ...
 *
 * const entries = groupAssetsRoutesHistoryByAsset(history, routesHistory);
 * ```
 *
 * @param assetsHistory - The page's history records, across every asset.
 * @param routesHistory - The page's aligned routes, keyed by request.
 * @returns The page's entries, keyed by asset.
 */
export const groupAssetsRoutesHistoryByAsset = (
  assetsHistory: HVSRecord[],
  routesHistory: GenericObject
): GenericObject => {
  const firstAssetId = assetsHistory?.[0]?.assetId;
  return (assetsHistory ?? []).reduce((entries, record) => {
    const key = `id-${record?.assetId ?? firstAssetId}`;
    const entry = entries[key] ?? {assetsHistory: [], routesHistory};
    entry.assetsHistory.push(record);
    entries[key] = entry;
    return entries;
  }, {} as GenericObject);
};

type LastAVLEvent = {
  dataSaved?: CVSAssetType[];
  data: CVSAssetType[];
  idField?: string;
  timeField?: string;
};

/**
 * @getLastAVLEvent
 * Retrieves the most recent AVL event for each unique item.
 *
 * This function processes a list of AVL event data to extract the latest event per item,
 * optionally merging it with an existing saved dataset. If `dataSaved` is provided,
 * the function ensures that each item is represented only by its latest event by
 * replacing outdated entries.
 *
 * @param {Object} params
 * @param {Array} params.data - New event data array
 * @param {Array} [params.dataSaved=[]] - Previously saved data (optional)
 * @param {string} [params.idField='assetId'] - Field name representing ID
 * @param {string} [params.timeField='eventTime'] - Field name representing event time
 * @returns {Array} - Updated list containing the most recent AVL events per item
 * 
 * ```typescript
 * 
 *   import {getLastAVLEvent} from '@/routes/tracking2/helpers';
 *  
 *   const avl = getLastAVLEvent(params);
 *
 * ```
 */
// groups records by `assetId` in one pass, keeping each group in its original
// order - `getLatestRecordByField` matches on `assetId` too, so a group holds
// exactly the records its filter would (a NaN id never matches there) ...
const groupRecordsByAssetId = (data: CVSAssetType[]): Map<unknown, CVSAssetType[]> => (
  data.reduce(function(groups, record) {
    const assetId = record?.assetId;
    if (!Number.isNaN(assetId)) {
      const group = groups.get(assetId);
      if (group) {
        group.push(record);
      } else {
        groups.set(assetId, [record]);
      }
    }
    return groups;
  }, new Map<unknown, CVSAssetType[]>())
);

export const getLastAVLEvent = ({
  data,
  dataSaved = [],
  idField = 'assetId',
  timeField = 'eventTime'
}: LastAVLEvent): CVSAssetType[] => {
  let lastEvents: CVSAssetType[] = [];

  if (Array.isArray(data) && data.length) {
    // the latest record per id is picked with the same sort as
    // `getLatestRecordByField`, but over that id's own records instead of
    // the whole list for every id - same result, without the O(n²) scan ...
    const groups       = groupRecordsByAssetId(data);
    const latestEvents = uniqBy(data, idField)
      .map(function(item) {
        return sortby(groups.get(item[idField]) ?? [], [timeField]).reverse()?.[0];
      })
      .filter(Boolean);

    // drops the saved records these latest events replace ...
    const assetIds      = new Set(latestEvents.map((e) => e[idField]));
    const filteredSaved = dataSaved.filter(
      (record) => !assetIds.has(record[idField])
    );

    lastEvents = [...filteredSaved, ...latestEvents];
  }

  return lastEvents;
};

/**
 * @mergeNodesIntoRoutes
 * 
 * Merge the last node into each route for every asset.
 * - Takes the last item from `nodes`
 * - Removes duplicate keys if they exist in `routes` (routes take priority)
 * - Updates `geometry` using `latitude` and `longitude` from routes
 * - Preserves the original `nodes` array
 * 
 * ```typescript
 * 
 *   import {mergeNodesIntoRoutes} from '@/routes/tracking2/helpers';
 *  
 *   const routes = mergeNodesIntoRoutes(assetRoutes);
 *
 * ```
 */
export const mergeNodesIntoRoutes = data => {
  if (isNil(data)) return data;

  const result = {};

  for (const [assetId, {nodes, routes}] of Object.entries(data)) {
    if (!nodes?.length || !routes?.length) {
      result[assetId] = {nodes, routes};
      continue;
    }

    const firstNode = nodes[0];
    const lastNode  = nodes[nodes.length - 1];

    result[assetId] = {
      nodes,
      routes : routes.map((route, index) => {
        // remove duplicate keys from lastNode
        const cleanedNode = {...firstNode};
        Object.keys(route).forEach(key => {
          if (key in cleanedNode) {
            delete cleanedNode[key];
          }
        });

        // choose eventTime
        const eventTime = index === routes.length - 1
          ? lastNode.eventTime
          : firstNode.eventTime;

        // merge, and update geometry with route lat/lon
        return {
          ...cleanedNode,
          ...route,
          eventTime,
          geometry : {
            x : route.longitude,
            y : route.latitude
          }
        };
      })
    };
  }

  return result;
};

/**
 * Creates a query definition by combining an optional filter with a base definition.
 * If a filter is provided, it appends it using the logical AND operator.
 * Otherwise, it returns the base definition as-is.
 * 
 * ```typescript
 * 
 *   import {createQueryDefinition} from '@/routes/tracking2/helpers';
 *  
 *   const queryDefinition = createQueryDefinition(filter, definition);
 *
 * ```
 */
export const createQueryDefinition = (filter: string, definition: string) => {
  return filter ? `${filter} AND ${definition}` : definition;
};

/**
 * Maps each item's `attributes` to a new object shape based on `keyMap`
 * and removes duplicates using the provided `uniqueKey`.
 *
 * `keyMap` defines how output properties are derived:
 * - string values map directly to attribute keys
 * - function values compute derived properties from attributes
 *
 * When multiple items share the same `uniqueKey`, the last occurrence wins.
 * This function is non-mutating and uses a Map to ensure uniqueness.
 * 
 * ```typescript
 * 
 *   import {mapAndDeduplicate} from '@/routes/tracking2/helpers';
 *  
 *   const map = mapAndDeduplicate(items, idKey, labelKey);
 *
 * ```
 */
export const mapAndDeduplicate = (
  items: any[],
  keyMap: Record<string, string>,
  uniqueKey: string) =>
  Array.from(
    new Map(
      items.map(({attributes}) => {
        const mapped = Object.entries(keyMap).reduce(
          (acc, [prop, attrKeyOrValue]) => {
            acc[prop] = attributes[attrKeyOrValue];
            return acc;
          },
          {}
        );

        return [mapped[uniqueKey], mapped];
      })
    ).values()
  );

/**
 * Returns a new array containing only items from `data1` whose `field` value
 * does NOT exist in `data2`.
 *
 * The comparison is performed using the provided `field` as a unique identifier.
 * This function is non-mutating and uses a Set for efficient lookups.
 * 
 * ``typescript
 * 
 *   import {excludeItemsByField} from '@/routes/tracking2/helpers';
 *  
 *   const data = excludeItemsByField(data1, data2, 'assetId');
 *
 * ```
 */
export const excludeItemsByField = <T, K extends keyof T>(
  data1: T[],
  data2: T[],
  field: K
): T[] => {
  if (data1?.length && data2?.length) {
    const values = new Set(data2.map(item => item[field]));
    return data1.filter(item => !values.has(item[field]));
  }
  return data1;
};

/**
 * Groups a collection of items by a given field and returns
 * an object where each key represents a unique field value
 * and the value is the number of occurrences.
 *
 * - Ignores items where the field value is empty or undefined
 * - Used to compare total vs selected counts per filter group
 */
const countItemsByField = <T>(items: T[], field: string): T[] => {
  return items.reduce((acc, item) => {
    const key = item[field];
    if (!key) return acc;

    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
};

type NotFullySelectedFilter<T> = {
  data: T[],
  selectedData: T[],
  filterData: T[],
  field: string
};

/**
 * Returns only filter items that are NOT fully selected.
 *
 * Logic:
 * - If a filter value does not exist in selectedData → include it
 * - If a filter value exists but not all related data items
 *   are selected → include it
 * - If all data items for a filter value are already selected
 *   → exclude it
 *
 * This allows partial selections while preventing filters
 * that are already fully applied from being shown again.
 * 
 * * ``typescript
 * 
 *   import {getNotFullySelectedFilters} from '@/routes/tracking2/helpers';
 *  
 *   const data = getNotFullySelectedFilters(data1, data2, data3, 'assetId');
 *
 * ```
 */
export const getNotFullySelectedFilters = <T>({
  data,
  selectedData,
  filterData,
  field
}: NotFullySelectedFilter<T>): T[] => {
  if (data?.length && selectedData?.length && filterData?.length) {
    const dataCountByField     = countItemsByField(data, field);
    const selectedCountByField = countItemsByField(selectedData, field);

    return filterData.filter(item => {
      const key = item[field];
      return (selectedCountByField[key] || 0) < (dataCountByField[key] || 0);
    });
  }
  return filterData;
};

/**
 * Asset History Report 
 */

export interface AssetHistoryStyleProps {
  offset?: number;
  topOffset?: number;
};

enum AssetHistoryOffset {
  FILTER_SELECTED_BAR_OPEN = 160,
  FILTER_SELECTED_BAR_COLLAPSED = 94,
  NO_FILTER_BAR_OPEN = 137,
  NO_FILTER_BAR_COLLAPSED = 57
};

enum AssetHistoryTopOffset {
  FILTER_SELECTED_BAR_OPEN = 35,
  FILTER_SELECTED_BAR_COLLAPSED = 47,
  NO_FILTER_BAR_COLLAPSED = 0
};

// Provides the offset for the asset history container based on the number of
// filters selected and whether the collapsible filter bar is open or not.
export const calculateContainerOffsets = (filtersSelected: number, isFilterBarOpen: boolean) => {
  if (isFilterBarOpen) {
    return {
      offset    : AssetHistoryOffset.FILTER_SELECTED_BAR_OPEN,
      topOffset : AssetHistoryTopOffset.FILTER_SELECTED_BAR_OPEN
    };
  }
  if (!isFilterBarOpen) {
    return {
      offset    : AssetHistoryOffset.FILTER_SELECTED_BAR_COLLAPSED,
      topOffset : AssetHistoryTopOffset.FILTER_SELECTED_BAR_COLLAPSED
    };
  }
  if (filtersSelected === 0 && isFilterBarOpen) {
    return {
      offset    : AssetHistoryOffset.NO_FILTER_BAR_OPEN,
      topOffset : AssetHistoryTopOffset.NO_FILTER_BAR_COLLAPSED
    };
  }
  return {
    offset    : AssetHistoryOffset.NO_FILTER_BAR_COLLAPSED,
    topOffset : AssetHistoryTopOffset.NO_FILTER_BAR_COLLAPSED
  };
};

/**
 * Groups a hash of asset histories into trip segments and sorts them.
 *
 * Assigns incremental `tripGroup` IDs based on ignition ON/OFF events,
 * then sorts all events by `eventTime` and `deviceMessageSequenceNumber`.
 *
 * ```typescript
 * import {onGroupHistory} from '@/routes/tracking2/helpers';
 *
 * const grouped = onGroupHistory(assetHistoryHash);
 * displayGrid(grouped);
 * ```
 * @param assetHistoryHash - Map of assetId to HVSRecord[].
 * @returns A flattened, sorted array of HVSRecord with `tripGroup` assigned.
 *
 */
export const onGroupHistory = (
  assetHistoryHash: GenericObject,
  tripGroupCountRef?: MutableRefObject<number>,
  enableTripServiceGrouping: boolean = false
): HVSRecord[] => {
  let group = tripGroupCountRef?.current ?? 0;
  let sortedHistory: HVSRecord[] = [];

  Object.values(assetHistoryHash).forEach((events: HVSRecord[]) => {
    group++;
    updateTripGroupCountRef(group, tripGroupCountRef);
    let inTrip = events[0]?.ignition === 'true';

    events.forEach(item => {
      if (!enableTripServiceGrouping) {
        if (item.ignition === 'true') {
          inTrip = true;
        }
        if (inTrip) {
          item.tripGroup = group;
        }
        if (item.ignition === 'false') {
          inTrip = false;
          group++;
          updateTripGroupCountRef(group, tripGroupCountRef);
        }
        item.tripGroup = item.tripGroup ?? null;
      } else {
        item.tripGroup = item.tripId ?? null;
      }
    });

    sortedHistory = sortedHistory.concat(
      sortby(events, ['eventTime', 'deviceMessageSequenceNumber'])
    );
  });
  
  return sortedHistory;
};

/*
* Update the trip group count ref when a valid ref is provided.
* This keeps the mutable ref synchronized with the latest count.
*/
const updateTripGroupCountRef = (count: number, tripGroupCountRef?: MutableRefObject<number>) => {
  if (!isNil(tripGroupCountRef?.current)) {
    tripGroupCountRef.current = count;
  }
};

type augmentHistoryDataProps = {
  resultsHistory: HVSRecord[];
  crashboxxEvents: any[];
  severeEventsList: HVSRecord[];
  systemUnits: any;
  tripGroupCountRef: MutableRefObject<number>;
  enableTripServiceGrouping?: boolean;
};

/**
 * Builds an augmented and grouped asset history structure from raw history and event inputs.
 *
 * Logic flow:
 * 1. Merges and normalizes history events by resolving crashboxx events into the base history.
 * 2. Sorts all resulting events chronologically by `eventTime` (ascending order).
 * 3. For each event:
 *    - Converts odometer-related fields (odometer, gpsOdometer, vbOdometer) into the target system units.
 *    - Adds UI-related flags:
 *        • gridVisibility = true (used to show item in grid view)
 *        • mapVisibility = true (used to render item on map by default)
 *    - Flags severe events based on whether the eventCode exists in `severeEventsList`.
 * 4. Groups all processed events by `assetId` into a hash map (assetHistoryHash).
 * 5. Transforms the grouped hash into a structured format via `onGroupHistory`.
 *
 * @returns A grouped and unit-normalized history structure organized by assetId.
 */
export const augmentHistoryData = ({
  systemUnits,
  resultsHistory,
  crashboxxEvents,
  severeEventsList,
  tripGroupCountRef,
  enableTripServiceGrouping = false
}: augmentHistoryDataProps) => {
  const assetHistoryHash: GenericObject = {};

  const historySorted = resolveHistoryEvents({
    history : resultsHistory,
    crashboxxEvents
  } as any).sort((a, b) => a?.eventTime - b?.eventTime);

  historySorted?.forEach((item) => {
    const {odometer, gpsOdometer, vbOdometer, systemOfUnits} = item ?? {};

    item = {
      ...item,
      odometer       : convertOdometer(odometer as any, systemOfUnits as any, systemUnits as any),
      gpsOdometer    : convertOdometer(gpsOdometer, systemOfUnits as any, systemUnits as any),
      vbOdometer     : convertOdometer(vbOdometer, systemOfUnits as any, systemUnits as any),
      gridVisibility : true,
      mapVisibility  : true,
      isSevereEvent  : severeEventsList.includes(item.eventCode as any)
    };

    // appended in place - copying the asset's whole list for every event
    // grows with the square of its events ...
    const asset = assetHistoryHash[item?.assetId];
    if (asset) {
      asset.push(item);
    } else {
      assetHistoryHash[item?.assetId] = [item];
    }
  });

  const sortedHistory = onGroupHistory(assetHistoryHash, tripGroupCountRef, enableTripServiceGrouping);
  
  return sortedHistory;
};

/**
 * Defines the readings a running Time Mode report is told apart by.
 */
export interface RunningTimeModeReportArgs {
  timeMode: TimeModes;
  isLiveHistoryActive: boolean;
  isHistoryActive: boolean;
  trackingMode: TrackingMode;
}

/**
 * Resolves which Time Mode report, if either, is actually running - read off
 * the mode as much as the flags, since the Live History widget runs on the
 * same flags in Live and is not a timeline of the control's, and a mode whose
 * panel is only asked for has nothing running under it yet.
 *
 * ```typescript
 * import {getRunningTimeModeReport} from '@/routes/tracking2/helpers';
 *
 * ...
 *
 * const runningReport = getRunningTimeModeReport({
 *   timeMode,
 *   isLiveHistoryActive,
 *   isHistoryActive,
 *   trackingMode
 * });
 * ```
 *
 * @param timeMode            - the mode the Time Mode control reads
 * @param isLiveHistoryActive - whether a timeline has been applied
 * @param isHistoryActive     - whether a History report has been applied
 * @param trackingMode        - the mode the map is tracking in
 * @returns The running report's mode, or `null` when neither is running.
 */
export const getRunningTimeModeReport = ({
  timeMode,
  isLiveHistoryActive,
  isHistoryActive,
  trackingMode
}: RunningTimeModeReportArgs): TimeModes.timeline | TimeModes.history | null => {
  let mode: TimeModes.timeline | TimeModes.history | null = null;
  if (timeMode === TimeModes.timeline && isLiveHistoryActive && trackingMode === TrackingMode.Live) {
    mode = TimeModes.timeline;
  } else if (timeMode === TimeModes.history && isHistoryActive && trackingMode === TrackingMode.History) {
    mode = TimeModes.history;
  }
  return mode;
};

/**
 * Defines the readings the applied filter bar's count is hidden by.
 */
export interface AppliedFilterCountArgs {
  filterCount: number;
  isTimelineChipVisible: boolean;
  isHistoryChipVisible: boolean;
}

/**
 * Defines what the applied filter count is taken from.
 */
export interface AppliedTrackingFilterCountArgs {
  // every selection applied across the filters, one per chip ...
  selectedCount: number;
  boundary?: unknown[];
  boundaryMode?: BoundaryMode;
  isDateChipVisible?: boolean;
  isFindNearbyActive?: boolean;
}

/**
 * Determines whether an applied boundary has a chip of its own - a drawn
 * boundary does, while a vicinity search is cleared from its own map button.
 *
 * @param boundary     - the applied boundary rings
 * @param boundaryMode - how the boundary was applied
 * @returns `true` when the boundary shows a chip, otherwise `false`.
 */
export const isBoundaryChipVisible = (boundary?: unknown[], boundaryMode?: BoundaryMode): boolean => (
  !!boundary?.length && boundaryMode !== BoundaryMode.Vicinity
);

/**
 * Counts the applied tracking filters the way the applied filter bar shows
 * them - one per selection chip, plus the Date, Boundary and Find Nearby
 * chips while they show - so the bar's own count and the side nav's filter
 * badge always agree.
 *
 * ```typescript
 * import {getAppliedTrackingFilterCount} from '@/routes/tracking2/helpers';
 *
 * ...
 *
 * const count = getAppliedTrackingFilterCount({
 *   selectedCount,
 *   boundary,
 *   boundaryMode,
 *   isDateChipVisible,
 *   isFindNearbyActive
 * });
 * ```
 *
 * @param args - the selections applied and the extra chips showing
 * @returns The number of filter chips applied, the mode chips aside.
 */
export const getAppliedTrackingFilterCount = ({
  selectedCount,
  boundary,
  boundaryMode,
  isDateChipVisible = false,
  isFindNearbyActive = false
}: AppliedTrackingFilterCountArgs): number => (
  selectedCount +
  (isDateChipVisible ? 1 : 0) +
  (isBoundaryChipVisible(boundary, boundaryMode) ? 1 : 0) +
  (isFindNearbyActive ? 1 : 0)
);

/**
 * Determines whether the applied filter bar's count is hidden - while the
 * Timeline or History chip is the only chip applied, the count of filters
 * would read 0 beside it, so it is left out instead.
 *
 * ```typescript
 * import {shouldHideAppliedFilterCount} from '@/routes/tracking2/helpers';
 *
 * ...
 *
 * const hideCount = shouldHideAppliedFilterCount({
 *   filterCount,
 *   isTimelineChipVisible,
 *   isHistoryChipVisible
 * });
 * ```
 *
 * @param filterCount           - the filter chips applied, the mode chips aside
 * @param isTimelineChipVisible - whether the Timeline chip is applied
 * @param isHistoryChipVisible  - whether the History chip is applied
 * @returns `true` while a mode chip is the only chip applied, otherwise `false`.
 */
export const shouldHideAppliedFilterCount = ({
  filterCount,
  isTimelineChipVisible,
  isHistoryChipVisible
}: AppliedFilterCountArgs): boolean => (
  !filterCount && (isTimelineChipVisible || isHistoryChipVisible)
);

/**
 * Defines the readings a History report over a vicinity search is told
 * apart by.
 */
export interface VicinityHistoryReportArgs {
  runningReport: TimeModes.timeline | TimeModes.history | null;
  boundaryMode: BoundaryMode;
  boundary?: unknown[];
}

/**
 * Determines whether the running report is a History report over an applied
 * vicinity search, in the current theme. A vicinity search is bounded by its
 * own radius and hours rather than by the fleet the filters select, so the
 * assets × days limit is not held against it - neither on the map nor in the
 * Tracking Filter - for as long as its Vicinity Search chip stands.
 *
 * ```typescript
 * import {isVicinityHistoryReport} from '@/routes/tracking2/helpers';
 *
 * ...
 *
 * const isVicinityHistory = isVicinityHistoryReport({
 *   runningReport,
 *   boundaryMode,
 *   boundary
 * });
 * ```
 *
 * @param runningReport - the running report, as `getRunningTimeModeReport` reads it
 * @param boundaryMode  - the mode the boundary filter was drawn in
 * @param boundary      - the boundary filter's applied rings
 * @returns `true` while a History report runs over an applied vicinity search
 *          in the current theme, otherwise `false`.
 */
export const isVicinityHistoryReport = ({
  runningReport,
  boundaryMode,
  boundary
}: VicinityHistoryReportArgs): boolean => (
  isCurrentTheme() &&
  runningReport === TimeModes.history &&
  boundaryMode === BoundaryMode.Vicinity &&
  !!boundary?.length
);

/**
 * Counts the whole days a History range spans, which is what the Time Mode
 * limit is counted against - a range that runs into a day counts that day,
 * and even the shortest range counts as one.
 *
 * ```typescript
 * import {toRangeDays} from '@/routes/tracking2/helpers';
 *
 * ...
 *
 * const days = toRangeDays(historyFrom, historyTo);
 * ```
 *
 * @param from - the range's start
 * @param to   - the range's end
 * @returns The whole days the range spans, at least one.
 */
export const toRangeDays = (from: Date, to: Date): number => (
  Math.max(HISTORY_MIN_DAYS, Math.ceil(moment(to).diff(moment(from), 'days', true)))
);
